import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { yieldToMain } from "@/lib/yield-to-main";

type RevealOptions = {
  threshold?: number;
  /** Class applied after first paint (starts the hidden/transition state). */
  prepareClass?: string;
  /** Class applied when the element enters the viewport. */
  activeClass?: string;
  /** Optional child selector that receives prepare/active classes instead of the host. */
  targetSelector?: string;
  /** When true, expose a `seen` flag (Counter). Avoid for pure visual reveals. */
  trackSeen?: boolean;
};

/**
 * IntersectionObserver-driven reveals without React re-renders on intersect.
 * ClassList writes avoid setState → commit → style invalidation → forced reflow.
 */
function useRevealClasses<T extends HTMLElement>(options: RevealOptions = {}) {
  const { threshold = 0.2, prepareClass, activeClass, targetSelector, trackSeen = false } = options;
  const ref = useRef<T | null>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const host = ref.current;
    if (!host || seen) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      if (trackSeen) setSeen(true);
      return;
    }

    let io: IntersectionObserver | undefined;
    let rafId = 0;
    let idleId = 0;

    const setup = () => {
      rafId = requestAnimationFrame(() => {
        const target =
          (targetSelector ? host.querySelector<HTMLElement>(targetSelector) : null) ?? host;
        if (prepareClass) target.classList.add(prepareClass);

        io = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              if (activeClass) target.classList.add(activeClass);
              if (trackSeen) setSeen(true);
              io?.disconnect();
            }
          },
          { threshold, rootMargin: "0px 0px -8% 0px" },
        );
        io.observe(host);
      });
    };

    // Yield past hydration / index.js eval before wiring observers.
    const start = () => {
      void yieldToMain().then(setup);
    };
    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(start, { timeout: 1_800 });
    } else {
      start();
    }

    return () => {
      if ("cancelIdleCallback" in window) window.cancelIdleCallback(idleId);
      cancelAnimationFrame(rafId);
      io?.disconnect();
    };
  }, [seen, threshold, prepareClass, activeClass, targetSelector, trackSeen]);

  return { ref, seen };
}

export function Reveal({
  children,
  delay = 0,
  className,
  as: Tag = "div",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: "div" | "li" | "section" | "article" | "p" | "h2";
}) {
  const { ref } = useRevealClasses<HTMLDivElement>({
    prepareClass: "reveal",
    activeClass: "reveal-in",
  });
  const Component = Tag as "div";
  return (
    <Component
      ref={ref}
      style={{ ["--reveal-delay" as string]: `${delay}ms` }}
      className={className}
    >
      {children}
    </Component>
  );
}

export function MaskImage({
  src,
  srcSet,
  alt,
  className,
  delay = 0,
  width,
  height,
  sizes,
}: {
  src: string;
  /** Responsive candidates, e.g. `${img640} 640w, ${img960} 960w`. */
  srcSet?: string;
  alt: string;
  className?: string;
  delay?: number;
  width?: number;
  height?: number;
  sizes?: string;
}) {
  const { ref } = useRevealClasses<HTMLDivElement>({
    threshold: 0.15,
    prepareClass: "mask-wipe",
    activeClass: "mask-wipe-in",
    targetSelector: "img",
  });
  return (
    <div ref={ref} className={cn("overflow-hidden", className)}>
      <img
        src={src}
        srcSet={srcSet}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        loading="lazy"
        decoding="async"
        style={{ ["--reveal-delay" as string]: `${delay}ms` }}
        className="h-full w-full object-cover"
      />
    </div>
  );
}

export function Counter({
  to,
  suffix = "",
  prefix = "",
  duration = 1600,
  label,
}: {
  to: number;
  suffix?: string;
  prefix?: string;
  duration?: number;
  label?: string;
}) {
  const { ref, seen } = useRevealClasses<HTMLSpanElement>({
    threshold: 0.4,
    trackSeen: true,
  });
  const textRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!seen) return;
    const el = textRef.current;
    if (!el) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.textContent = `${prefix}${to.toLocaleString()}${suffix}`;
      return;
    }

    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      // Direct DOM write — avoids React re-render layout thrashing every frame.
      el.textContent = `${prefix}${Math.round(to * eased).toLocaleString()}${suffix}`;
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [seen, to, duration, prefix, suffix]);

  const accessible = label ?? `${prefix}${to}${suffix}`;
  return (
    <span ref={ref} className="tabular-nums">
      <span ref={textRef} aria-hidden="true">
        {prefix}0{suffix}
      </span>
      <span className="sr-only">{accessible}</span>
    </span>
  );
}
